import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import PageHeader from "../components/PageHeader";
import { KpiSkeleton } from "../components/Skeletons";
import {
  BarChart3, TrendingUp, TrendingDown, Users, UserPlus, Target,
  DollarSign, Loader2, Activity, ShoppingCart, FileText, Share2,
  ArrowUp, ArrowDown, Minus, RefreshCw
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart
} from "recharts";

const COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
const FUNNEL_COLORS = { nouveau: "#3b82f6", contacte: "#f59e0b", qualifie: "#8b5cf6", converti: "#10b981", perdu: "#ef4444" };
const CRM_COLORS = { prospect: "#64748b", qualification: "#3b82f6", proposition: "#f59e0b", negociation: "#8b5cf6", closing: "#ec4899", gagne: "#10b981", perdu: "#ef4444" };

function KpiCard({ icon: Icon, label, value, subvalue, trend, color = "#10b981", testId }) {
  const isUp = trend > 0;
  const isDown = trend < 0;
  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <div className="flex items-center gap-2 mt-1">
        {subvalue && <span className="text-[10px] text-slate-500">{subvalue}</span>}
        {trend !== undefined && trend !== null && (
          <span className={`text-[10px] font-medium flex items-center gap-0.5 ${isUp ? "text-emerald-400" : isDown ? "text-red-400" : "text-slate-500"}`}>
            {isUp ? <ArrowUp className="w-2.5 h-2.5" /> : isDown ? <ArrowDown className="w-2.5 h-2.5" /> : <Minus className="w-2.5 h-2.5" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  );
}

function fmt(v) { return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v); }

export default function AnalyticsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, act] = await Promise.all([
        api.get("/analytics/dashboard"),
        api.get("/analytics/activity-feed"),
      ]);
      setData(dash.data);
      setFeed(act.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-4 md:p-6 max-w-[1400px] mx-auto"><KpiSkeleton count={4} testId="analytics-loading-skeleton" /></div>;
  if (!data) return <div className="p-6 text-slate-400">Erreur de chargement</div>;

  const { kpis, financier, evolution, funnel_prospects, crm_pipeline, marketing, facturation } = data;

  const FEED_ICONS = { prospect: UserPlus, crm: Target, encaissement: DollarSign, marketing: Share2, login: Activity };
  const FEED_COLORS = { prospect: "#3b82f6", crm: "#8b5cf6", encaissement: "#10b981", marketing: "#ec4899", login: "#64748b" };

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="analytics-page">
      <PageHeader
        icon={<BarChart3 />}
        title="Analytics & Reporting"
        description="Vue d'ensemble en temps réel de votre activité"
        testId="analytics-header"
        actions={(
          <Button onClick={load} variant="outline" className="border-white/10 text-slate-400 text-xs" data-testid="refresh-analytics">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualiser
          </Button>
        )}
      />

      {/* KPIs Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard icon={Users} label="Clients" value={kpis.total_clients} subvalue={`+${kpis.new_clients_month} ce mois`} color="#3b82f6" testId="kpi-clients" />
        <KpiCard icon={UserPlus} label="Prospects" value={kpis.total_prospects} subvalue={`+${kpis.new_prospects_month} ce mois`} color="#8b5cf6" testId="kpi-prospects" />
        <KpiCard icon={DollarSign} label="CA du mois" value={fmt(financier.ca_mois)} subvalue={`Prec: ${fmt(financier.ca_prev_mois)}`} trend={financier.variation_mois} color="#10b981" testId="kpi-ca-mois" />
        <KpiCard icon={Target} label="Pipeline CRM" value={fmt(kpis.pipeline_weighted)} subvalue={`${kpis.taux_win_crm}% win rate`} color="#f59e0b" testId="kpi-pipeline" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* CA Evolution */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="chart-evolution">
          <h3 className="text-xs font-bold mb-3 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-400" /> Evolution CA (12 mois)
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={evolution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1b3a" />
                <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11, color: "#fff" }} />
                <Area type="monotone" dataKey="encaissements" stroke="#10b981" fill="#10b981" fillOpacity={0.15} strokeWidth={2} name="Encaissements" />
                <Area type="monotone" dataKey="depenses" stroke="#ef4444" fill="#ef4444" fillOpacity={0.1} strokeWidth={2} name="Depenses" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Funnel Prospects */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="chart-funnel">
          <h3 className="text-xs font-bold mb-3 flex items-center gap-2">
            <UserPlus className="w-3.5 h-3.5 text-blue-400" /> Funnel Prospects
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnel_prospects} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1b3a" />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis type="category" dataKey="stage" tick={{ fill: "#94a3b8", fontSize: 10 }} width={80} />
                <Tooltip contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11, color: "#fff" }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Nombre">
                  {funnel_prospects.map((entry, idx) => (
                    <Cell key={idx} fill={FUNNEL_COLORS[entry.stage] || COLORS[idx % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Second Row: CRM Pipeline + Stats Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* CRM Pipeline */}
        <div className="lg:col-span-2 bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="chart-crm-pipeline">
          <h3 className="text-xs font-bold mb-3 flex items-center gap-2">
            <Target className="w-3.5 h-3.5 text-amber-400" /> Pipeline CRM
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={crm_pipeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1b3a" />
                <XAxis dataKey="stage" tick={{ fill: "#64748b", fontSize: 9 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11, color: "#fff" }} formatter={(v) => fmt(v)} />
                <Bar dataKey="amount" radius={[4, 4, 0, 0]} name="Montant">
                  {crm_pipeline.map((entry, idx) => (
                    <Cell key={idx} fill={CRM_COLORS[entry.stage] || COLORS[idx % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="space-y-3">
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="stat-facturation">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-blue-400" />
              <span className="text-[10px] text-slate-500 uppercase">Facturation</span>
            </div>
            <p className="text-lg font-bold text-white">{facturation.total} factures</p>
            <div className="flex gap-3 mt-1.5">
              <span className="text-[10px] text-emerald-400">{facturation.payees} payees</span>
              <span className="text-[10px] text-red-400">{facturation.impayees} impayees</span>
            </div>
            <div className="w-full bg-white/5 rounded-full h-1.5 mt-2">
              <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${facturation.taux_paiement}%` }} />
            </div>
            <span className="text-[9px] text-slate-500">{facturation.taux_paiement}% de recouvrement</span>
          </div>

          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="stat-marketing">
            <div className="flex items-center gap-2 mb-2">
              <Share2 className="w-4 h-4 text-pink-400" />
              <span className="text-[10px] text-slate-500 uppercase">Marketing</span>
            </div>
            <p className="text-lg font-bold text-white">{marketing.total_posts} posts</p>
            <div className="flex gap-3 mt-1.5">
              <span className="text-[10px] text-emerald-400">{marketing.posts_publies} publies</span>
              <span className="text-[10px] text-blue-400">{marketing.posts_ce_mois} ce mois</span>
            </div>
          </div>

          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="stat-ca-annuel">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-emerald-400" />
              <span className="text-[10px] text-slate-500 uppercase">CA Annuel</span>
            </div>
            <p className="text-lg font-bold text-white">{fmt(financier.ca_annuel)}</p>
            <span className={`text-[10px] flex items-center gap-0.5 mt-1 ${financier.variation_annuel >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {financier.variation_annuel >= 0 ? <ArrowUp className="w-2.5 h-2.5" /> : <ArrowDown className="w-2.5 h-2.5" />}
              {Math.abs(financier.variation_annuel)}% vs N-1
            </span>
          </div>
        </div>
      </div>

      {/* Activity Feed */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="activity-feed">
        <h3 className="text-xs font-bold mb-3 flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-[#f7c935]" /> Activite recente (7 jours)
        </h3>
        {feed.length === 0 ? (
          <p className="text-[10px] text-slate-500 py-4 text-center">Aucune activite recente</p>
        ) : (
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {feed.map((item, idx) => {
              const Icon = FEED_ICONS[item.type] || Activity;
              const color = FEED_COLORS[item.type] || "#64748b";
              const d = item.at ? new Date(item.at) : null;
              return (
                <div key={idx} className="flex items-center gap-2.5 py-1.5 border-b border-white/[0.03] last:border-0">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
                    <Icon className="w-3 h-3" style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-white truncate">{item.title}</p>
                    {item.detail && <p className="text-[9px] text-slate-500 truncate">{item.detail}</p>}
                  </div>
                  {d && <span className="text-[9px] text-slate-600 flex-shrink-0">{d.toLocaleDateString("fr-FR")} {d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
